import React, { useRef, useState, useEffect, useCallback } from 'react';
import { saveAs } from 'file-saver';
import PropTypes from 'prop-types';
import useUserStore from '../store/userStore';
import usePNGHandler from './usePNGHandler';

/**
 * Кнопка "Download PNG"
 *
 * @param {Object[]} assets
 * @param {string} archiveName
 * @param {boolean} is_purchased
 * @param {boolean} isSubscriptionActive
 * @param {function} onBeforeAction    // если вернёт (или зарезолвит) false, не скачиваем
 * @param {function} onActionComplete  // вызываем после успешного скачивания
 * @param {boolean} debug
 */
const DownloadPNGButton = ({
  assets,
  debug = false,
  archiveName,
  is_purchased,
  isSubscriptionActive,
  onBeforeAction,
  onActionComplete,
}) => {
  if (debug) {
    console.log('[DownloadPNGButton] props:', {
      assets,
      debug,
      archiveName,
      is_purchased,
      isSubscriptionActive
    });
  }

  const accessToken = useUserStore((state) => state.accessToken);
  const isLoggedIn = useUserStore((state) => state.isLoggedIn);

  const {
    isDownloading,
    setIsDownloading,
    errorMessage,
    setErrorMessage,
    dropdownSizes,
    freeSizes,
    defaultSize,
    handleDownloadLogic,
    saveSelectedSize,
    collectionName
  } = usePNGHandler(assets, accessToken, isLoggedIn, is_purchased, isSubscriptionActive, debug, archiveName);

  const [showDropdown, setShowDropdown] = useState(false);
  const [arrowHover, setArrowHover] = useState(false);
  const [hoveredSize, setHoveredSize] = useState(null);

  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Упрощённая логика скачивания
  const startDownload = useCallback(async (downloadSize) => {
    if (debug) console.log('[DownloadPNGButton] startDownload with:', downloadSize);
    setIsDownloading(true);
    try {
      const result = await handleDownloadLogic(downloadSize);
      if (debug) console.log('[DownloadPNGButton] handleDownloadLogic result:', result);

      if (result.type === 'single' || result.type === 'archive') {
        saveAs(result.blob, result.fileName);

        // Сообщаем, что скачивание завершилось УСПЕШНО
        if (onActionComplete) {
          onActionComplete();
        }

        // (Если у вас была логика показа MakeaLinkPopup, можно оставить)
        if (!is_purchased && !isSubscriptionActive) {
          // ...
        }
      }
    } catch (error) {
      if (debug) console.log('[DownloadPNGButton] Error:', error);
      setErrorMessage(`Error downloading PNG: ${error.message}`);
    } finally {
      setIsDownloading(false);
      setShowDropdown(false);
    }
  }, [
    debug,
    handleDownloadLogic,
    is_purchased,
    isSubscriptionActive,
    onActionComplete,
    setErrorMessage,
    setIsDownloading
  ]);

  /**
   *  Вызывается при клике по конкретному размеру в выпадающем меню.
   *  1) Проверяем onBeforeAction
   *  2) Если false — прерываем
   *  3) Иначе стартуем скачивание
   */
  const handleDownload = useCallback(async (downloadSize) => {
    if (onBeforeAction) {
      const canContinue = await Promise.resolve(onBeforeAction());
      if (!canContinue) {
        return;
      }
    }

    startDownload(downloadSize);
  }, [onBeforeAction, startDownload]);

  // Клик по основной кнопке (defaultSize)
  const handleDefaultDownload = async (e) => {
    e.stopPropagation();
    if (!defaultSize) {
      setErrorMessage("No default size available for this collection.");
      return;
    }
    // Проверяем onBeforeAction
    if (onBeforeAction) {
      const canContinue = await Promise.resolve(onBeforeAction());
      if (!canContinue) {
        return;
      }
    }
    // Продолжаем скачивание
    handleDownload(defaultSize);
  };

  // Клик по пункту в выпадающем списке
  const handleSizeSelection = (size) => {
    if (!collectionName) {
      return;
    }
    saveSelectedSize(collectionName, size);
    handleDownload(size);
  };

  const handleToggleDropdown = (e) => {
    e.stopPropagation();
    setShowDropdown(!showDropdown);
  };

  return (
    <div className="inline-block relative w-full" ref={dropdownRef}>
      <button
        className="block w-full text-center whitespace-nowrap px-3 py-1 bg-transparent text-black border border-black shadow-md rounded-md disabled:opacity-50 disabled:cursor-not-allowed min-h-[55px]"
        disabled={isDownloading}
        onClick={handleDefaultDownload}
      >
        {isDownloading
          ? 'Downloading...'
          : `Download ${defaultSize ? `${defaultSize}x${defaultSize} ` : ''}PNG`}
        <span
          className={`ml-2 text-[0.8em] cursor-pointer inline-block rounded-full w-[30px] h-[30px] leading-[33px] text-center select-none transition-colors duration-200 ${
            arrowHover ? 'bg-gray-200' : 'bg-transparent'
          }`}
          onClick={handleToggleDropdown}
          onMouseEnter={() => setArrowHover(true)}
          onMouseLeave={() => setArrowHover(false)}
        >
          ▼
        </span>
      </button>

      {showDropdown && (
        <div className="absolute top-[calc(100%+2px)] left-0 bg-white border border-gray-300 rounded-md z-[999] p-[5px] box-border min-w-full">
          {dropdownSizes.map((size) => {
            const isFree = freeSizes.includes(size);
            // Если не куплено и нет подписки, а размер не free → отключаем
            const isDisabled = !isFree && !is_purchased && !isSubscriptionActive;
            const isHovered = hoveredSize === size;

            return (
              <div
                key={size}
                onMouseEnter={() => setHoveredSize(size)}
                onMouseLeave={() => setHoveredSize(null)}
                onClick={() => {
                  if (!isDisabled) {
                    handleSizeSelection(size);
                  }
                }}
                title={
                  isDisabled
                    ? 'All formats and sizes available after purchase.'
                    : undefined
                }
                className={`px-5 py-2.5 whitespace-nowrap transition-all duration-200 ${
                  isDisabled 
                    ? 'cursor-not-allowed text-gray-400 opacity-50' 
                    : isHovered 
                      ? 'cursor-pointer text-white bg-black rounded' 
                      : 'cursor-pointer text-black bg-transparent'
                }`}
              >
                {size}px
              </div>
            );
          })}
        </div>
      )}

      {errorMessage && (
        <p className="text-red-500 mt-[10px]">{errorMessage}</p>
      )}
    </div>
  );
};

DownloadPNGButton.propTypes = {
  assets: PropTypes.arrayOf(
    PropTypes.shape({
      slug: PropTypes.string.isRequired,
      collection: PropTypes.string.isRequired,
      media_type: PropTypes.string,
      is_free: PropTypes.bool,
      format: PropTypes.arrayOf(PropTypes.string).isRequired,
    })
  ).isRequired,
  debug: PropTypes.bool,
  archiveName: PropTypes.string,
  is_purchased: PropTypes.bool,
  isSubscriptionActive: PropTypes.bool,
  /** Колбэк: если вернёт (или зарезолвит) false, останавливаем скачивание */
  onBeforeAction: PropTypes.func,
  /** Колбэк: вызывается после успешного скачивания */
  onActionComplete: PropTypes.func,
};

DownloadPNGButton.defaultProps = {
  debug: false,
  archiveName: '',
  is_purchased: false,
  isSubscriptionActive: false, 
  onBeforeAction: null,
  onActionComplete: null,
};

export default DownloadPNGButton;